import { ActivatedRouteSnapshot, Router } from "@angular/router";
import { catchError, tap } from "rxjs";
import { ApiService } from "../api/api.service";
import { AuthService } from "./services/auth.service";
import { inject } from "@angular/core";
import { StrapiService } from "../api/strapi.service";
import { PlanTemplatesService } from "./services/plan-templates.service";

const handlerResolverNotFoundError = (route: Router) => async (error: any) => {
  await route.navigate(['/404']);
  throw error;
};

const handlerResolverError = (route: Router) => async (error: any) => {
  const authService: AuthService = inject(AuthService);
  switch (error.status) {
    // 6 === 403
    case 409:{
      switch (error.error.code) {
        case 0: {
          console.log('default');
          break;
        }
        case 6: {
          await route.navigate(['/403']);
          break;
        }
        default: {
          console.log('default');
          break;
        }
      }
      break;
    }
    case 401:{
      switch (error.error.code) {
        case 33: {
          await route.navigate(['/choose-account'], { queryParams: {type: 'blocked'}});
          break;
        }
        case 34: {
          await route.navigate(['/choose-account'], { queryParams: {type: 'revoked'}});
          break;
        }
        case 43: {
          await route.navigate(['/403']);
          break;
        }
        case 44: {
          await route.navigate(['/access-denied']);
          break;
        }
        case 45: {
          await route.navigate(['/403']);
          break;
        }
        default: {        
          authService.logout(() => {
            route.navigate(['/login']).then();
          });
          break;
        }
      }
      break;
    }
    case 403:{
      switch (error.error?.code) {
        case 46: {
          await route.navigate(['/access-denied']);
          break;
        }
        default: {
          await route.navigate(['/403']);
          break;
        }
      }
      break;
    }
    default: {
      await route.navigate(['/something-went-wrong']);
      break;
    }
  }
  throw error;
};

const handlerEmailCodeResponseError = (route: Router) => async (error: any) => {
  await route.navigate(['/invite-expired']);
  throw error;
};

export const subscribedWallets = () => {
  const router: Router = inject(Router);
  return inject(ApiService).getSubscribedWallets().pipe(catchError(handlerResolverError(router)));
};

export const getOperations = () => {
  const router: Router = inject(Router);
  return inject(ApiService).getOperations().pipe(catchError(handlerResolverError(router)));
};

export const getInvoices = () => {
  const router: Router = inject(Router);
  return inject(ApiService).getInvoices().pipe(catchError(handlerResolverError(router)));
};

export const getPlanTemplates = () => {
  const router: Router = inject(Router);
  return inject(PlanTemplatesService).getPlanTemplates().pipe(catchError(handlerResolverError(router)));
};

export const getTableSettings = <T>(tableName: string) => () => {
  const authService: AuthService = inject(AuthService);
  const apiService = inject(ApiService);
  const user = authService.accountDto();
  if (!user) return;
  const { uuid } = user;
  return apiService.getTableSettingsByUserIdAndTableName<T>(uuid, tableName);
}

export const getEmailCodeResponse = (activatedRouteSnapshot: ActivatedRouteSnapshot) => {
  const inviteCode = activatedRouteSnapshot.queryParamMap.get('inviteCode');
  const router = inject(Router);
  if (!inviteCode) return null;
  return inject(ApiService).getEmailCodeResponse(inviteCode).pipe(tap(async ({ isUsed }) => {
    // Если inviteCode уже использован, то перенаправляем на главную
    if (isUsed) {
      await router.navigate(['/']);
    }
  }), catchError(handlerEmailCodeResponseError(router)));
};

export const getPrivacyPolicy = () => {
  const router: Router = inject(Router);
  return inject(StrapiService).getPrivacyPolicy().pipe(catchError(handlerResolverNotFoundError(router)));
};

export const getTermsOfUse = () => {
  const router: Router = inject(Router);
  return inject(StrapiService).getTermsOfUse().pipe(catchError(handlerResolverNotFoundError(router)));
};

export const getEisDashboardBlockchainStatistics = () => {
  const router: Router = inject(Router);
  return inject(ApiService).getEisDashboardBlockchainStatistics().pipe(catchError(handlerResolverError(router)));
};

export const getEisDashboardStatistics = () => {
  const router: Router = inject(Router);
  return inject(ApiService).getEisDashboardBlockStatistic().pipe(catchError(handlerResolverError(router)));
};

export const getApiMetrics = () => {
  const router: Router = inject(Router);
  return inject(ApiService).getApiMetrics().pipe(catchError(handlerResolverError(router)));
};
